// RankedBars — the reusable ranked horizontal bar list (Phase 3).
//
// The mechanical half of the ranked-bar data-join type (Top Scorers, Assists,
// and more to come): one absolutely-positioned row per item, sorted by the
// caller, bars scaled to `max`, rows animating to their new position + width on
// a data change. Extracted when Assists became the second consumer (mirrors how
// League + Form Table share <StandingsTable>).
//
// The split: React owns the `.tsl-wrap` container (and renders nothing inside
// it); **D3 owns the spacer + the keyed `.tsl-row` join** in a useEffect — a
// verbatim port of the legacy renderers, so rows animate instead of snapping.
// All *presentation* (bar colour, highlight, label/count colour) is decided by
// the caller and arrives per-row; RankedBars only positions + animates. Emits
// the exact legacy DOM + classes (verified by each consumer's visual A/B).

import { useEffect, useLayoutEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { easeCubicInOut, easeCubicOut } from "d3-ease";
import { barWidthPct } from "@bento/shared";

const ROW_H = 24; // 16px bar + 8px gap
const DUR = 450;

/** One row with its presentation already resolved by the caller. */
export interface RankedBarRow {
  name: string;
  value: number;
  /** Toggle `bento-selected` on the bar (fixture-team highlight). */
  selected: boolean;
  /** Inline bar background; omit to let the CSS bar-fill class colour it. */
  bg?: string;
  /** Inline label colour; "" = CSS default. */
  labelColor: string;
  /** Inline count colour; "" = CSS default. */
  countColor: string;
}

export interface RankedBarsProps {
  rows: RankedBarRow[];
  /** Bar-width denominator (the leader's value); ≥1. */
  max: number;
  /** Full class string for `.bar-fill` on enter, e.g. "bar-fill blue bento-fill". */
  barFillClass: string;
}

export default function RankedBars({ rows, max, barFillClass }: RankedBarsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const firstRef = useRef(true);

  // The legacy renderers left-align the stack by setting justify-content on the
  // module body (the React root host). Replicate + restore on unmount (the
  // Penalty-Takers host-tag pattern); Phase 4 makes this body JSX.
  useLayoutEffect(() => {
    const body = wrapRef.current?.parentElement;
    if (!body) return;
    const prev = body.style.justifyContent;
    body.style.justifyContent = "flex-start";
    return () => {
      body.style.justifyContent = prev;
    };
  }, []);

  // A signature of everything the D3 render depends on, so the effect re-runs on
  // any data/presentation change (rows are a fresh array each parent render).
  const sig =
    barFillClass +
    "|" +
    max +
    "|" +
    rows
      .map(
        (r) =>
          `${r.name}:${r.value}:${r.selected ? 1 : 0}:${r.bg ?? ""}:${r.labelColor}:${r.countColor}`
      )
      .join(",");

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const isFirst = firstRef.current;

    // Spacer (D3-owned) drives the scrollable height so absolutely-positioned
    // rows overflow naturally. Create once, then just update its height.
    let spacer = wrap.querySelector<HTMLDivElement>(".tsl-spacer");
    if (!spacer) {
      spacer = document.createElement("div");
      spacer.className = "tsl-spacer";
      spacer.style.pointerEvents = "none";
      wrap.appendChild(spacer);
    }
    spacer.style.height = Math.max(0, rows.length * ROW_H - 8) + "px";

    const rowSel = select<HTMLElement, unknown>(wrap)
      .selectAll<HTMLDivElement, RankedBarRow>(".tsl-row")
      .data(rows, (d) => d.name);

    // ENTER — build a row's structure once per item.
    const entered = rowSel
      .enter()
      .append("div")
      .attr("class", "tsl-row")
      .style("position", "absolute")
      .style("left", "0")
      .style("right", "0")
      .style("height", "16px")
      .style("display", "flex")
      .style("align-items", "center")
      .style("gap", "6px")
      .style("opacity", isFirst ? "0" : "1")
      .style("top", (_d, i) => i * ROW_H + "px");

    const bgDiv = entered
      .append("div")
      .attr("class", "bar-bg")
      .style("flex", "1")
      .style("min-width", "0");
    bgDiv.append("div").attr("class", barFillClass).style("width", "0%");
    bgDiv.append("div").attr("class", "bar-label");
    entered
      .append("span")
      .attr("class", "tsl-count")
      .style("font-size", "var(--t-data)")
      .style("width", "18px")
      .style("text-align", "right")
      .style("flex-shrink", "0");

    const all = entered.merge(rowSel);

    // Colours, label + count text update immediately (no transition), read from
    // the caller-resolved presentation.
    all.each(function (this: HTMLDivElement, d: RankedBarRow) {
      const fill = this.querySelector<HTMLElement>(".bar-fill")!;
      const label = this.querySelector<HTMLElement>(".bar-label")!;
      const count = this.querySelector<HTMLElement>(".tsl-count")!;
      fill.classList.toggle("bento-selected", d.selected);
      if (d.bg !== undefined) fill.style.background = d.bg;
      label.textContent = d.name;
      label.style.color = d.labelColor;
      count.textContent = String(d.value);
      count.style.color = d.countColor;
    });

    // Animate row positions (and fade-in with a stagger on first render).
    all
      .transition("pos")
      .duration(DUR)
      .ease(easeCubicInOut)
      .delay((_d, i) => (isFirst ? i * 18 : 0))
      .style("opacity", "1")
      .style("top", (_d, i) => i * ROW_H + "px");

    // Animate bar widths in sync with the row movement.
    all
      .select(".bar-fill")
      .transition("bar")
      .duration(DUR)
      .ease(easeCubicOut)
      .delay((_d, i) => (isFirst ? i * 18 : 0))
      .style("width", (d: RankedBarRow) => barWidthPct(d.value, max) + "%");

    // EXIT — Club variants swap their whole list on a team change.
    rowSel.exit().transition().duration(250).style("opacity", "0").remove();

    firstRef.current = false;
    // `sig` encodes every input the render above reads (rows content, max,
    // barFillClass), so it is the complete dependency.
  }, [sig]);

  return (
    <div
      className="tsl-wrap"
      ref={wrapRef}
      style={{ position: "relative", width: "100%" }}
    />
  );
}
